import { React, useEffect, useState } from "react";
import AppWrapper from "../../AppWrapper";
import { useNavigate } from "react-router-dom";
import supabase from "../../../supabase-client";
import {
  Page,
  Button,
  LegacyCard,
  IndexTable,
  Text,
  Pagination,
  TextField,
  BlockStack,
  Spinner,
  Badge,
  InlineStack,
} from "@shopify/polaris";
import { SearchIcon } from "@shopify/polaris-icons";
import { blockReasonLabel } from "../../../utility/clientBlocks";

function Companies() {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  const pageSize = 20;

  const fetchCompanies = async () => {
    setLoading(true);
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;

    let query = supabase
      .from("companies")
      .select(
        "id, name, rfc, contact_name, contact_phone, contact_email, blocked, block_reason"
      )
      .order("name");

    const term = search.trim();
    if (term) {
      const ors = [
        `name.ilike.%${term}%`,
        `rfc.ilike.%${term}%`,
        `contact_name.ilike.%${term}%`,
        `contact_phone.ilike.%${term}%`,
        `contact_email.ilike.%${term}%`,
      ];
      // Pure digits → also match the company id exactly.
      if (/^\d+$/.test(term)) ors.push(`id.eq.${term}`);
      query = query.or(ors.join(","));
    }

    const { data, error } = await query.range(from, to);

    if (error) {
      console.error(error.message);
      setLoading(false);
      return;
    }

    setCompanies(data);
    setHasMore(data?.length >= pageSize);
    setLoading(false);
  };

  useEffect(() => {
    fetchCompanies();
  }, [page]);

  const rowMarkup = companies.map((c, index) => (
    <IndexTable.Row
      id={c.id}
      key={c.id}
      position={index}
      onClick={() => navigate(`/companies/edit?id=${c.id}`)}
    >
      <IndexTable.Cell>
        <InlineStack gap="200" blockAlign="center" wrap={false}>
          <Text fontWeight="semibold">{c.name}</Text>
          {c.blocked && (
            <Badge tone="critical">
              {`Bloqueada · ${blockReasonLabel(c.block_reason)}`}
            </Badge>
          )}
        </InlineStack>
      </IndexTable.Cell>
      <IndexTable.Cell>{c.rfc || "-"}</IndexTable.Cell>
      <IndexTable.Cell>{c.contact_name || "-"}</IndexTable.Cell>
      <IndexTable.Cell>{c.contact_phone || "-"}</IndexTable.Cell>
      <IndexTable.Cell>{c.contact_email || "-"}</IndexTable.Cell>
    </IndexTable.Row>
  ));

  return (
    <AppWrapper>
      <Page
        title="Empresas"
        primaryAction={
          <Button
            variant="primary"
            onClick={() => navigate("/companies/edit")}
          >
            Crear Empresa
          </Button>
        }
      >
        <BlockStack gap={400}>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              setPage(1);
              fetchCompanies();
            }}
          >
            <div style={{ display: "flex", gap: "0.25rem" }}>
              <span style={{ flexGrow: 1 }}>
                <TextField
                  autoComplete="off"
                  placeholder="Buscar por nombre, RFC, contacto, teléfono, correo o #ID"
                  value={search}
                  onChange={(value) => setSearch(value)}
                  disabled={loading}
                />
              </span>
              <span
                style={{
                  aspectRatio: 1,
                  display: "flex",
                  alignItems: "center",
                  width: 32,
                  height: 32,
                }}
              >
                <Button icon={SearchIcon} submit disabled={loading} />
              </span>
            </div>
          </form>
          {loading ? (
            <Spinner size="small" />
          ) : (
            <LegacyCard>
              <>
                <IndexTable
                  resourceName={{
                    singular: "Empresa",
                    plural: "Empresas",
                  }}
                  selectable={false}
                  itemCount={companies?.length ?? 0}
                  headings={[
                    { title: "Nombre" },
                    { title: "RFC" },
                    { title: "Contacto" },
                    { title: "Teléfono" },
                    { title: "Correo" },
                  ]}
                >
                  {rowMarkup}
                </IndexTable>
                <div
                  style={{
                    padding: "1rem",
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <Pagination
                    hasPrevious={page > 1}
                    onPrevious={() => setPage(page - 1)}
                    hasNext={hasMore}
                    onNext={() => setPage(page + 1)}
                  />
                </div>
              </>
            </LegacyCard>
          )}
        </BlockStack>
      </Page>
    </AppWrapper>
  );
}

export default Companies;
